import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { adminApi } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { RECIPE_CATEGORIES, RECIPE_TAGS, NUTRITION_KEYS } from "@/lib/recipes";
import { ImageUpload } from "@/components/ImageUpload";

const linesToList = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

export const RecipeEditor = ({ recipe, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: recipe.name,
    image: recipe.image,
    category: recipe.category,
    tags: recipe.tags || [],
    intro: recipe.intro,
    prep_time: recipe.prep_time,
    cook_time: recipe.cook_time,
    total_time: recipe.total_time,
    serves: recipe.serves,
    ingredients: recipe.ingredients.join("\n"),
    steps: recipe.steps.join("\n"),
    nutrition: { ...recipe.nutrition },
    tips: (recipe.tips || []).join("\n"),
    portion: recipe.portion,
    suitable_for: (recipe.suitable_for || []).join("\n"),
    active: recipe.active !== false,
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setNut = (k) => (e) => setForm((f) => ({ ...f, nutrition: { ...f.nutrition, [k]: e.target.value } }));
  const toggleTag = (t) => setForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));

  const save = async () => {
    if (!form.name.trim() || !form.intro.trim()) {
      toast.error("Name and introduction are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        ingredients: linesToList(form.ingredients),
        steps: linesToList(form.steps),
        tips: linesToList(form.tips),
        suitable_for: linesToList(form.suitable_for),
      };
      const res = recipe.id ? await adminApi.put(`/recipes/${recipe.id}`, payload) : await adminApi.post("/recipes", payload);
      onSaved(res.data, !recipe.id);
      toast.success(recipe.id ? "Recipe updated" : "Recipe added");
      onClose();
    } catch {
      toast.error("Could not save recipe");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[22rem] max-h-[85vh] overflow-y-auto rounded-3xl p-5" data-testid="recipe-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-emerald-950 dark:text-emerald-50">{recipe.id ? "Edit Recipe" : "Add Recipe"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-1">
          <Field label="Recipe name" testid="recipe-edit-name" value={form.name} onChange={set("name")} />
          <ImageUpload value={form.image} onChange={(v) => setForm((f) => ({ ...f, image: v }))} testid="recipe-edit-image" />
          <SelectField label="Category" testid="recipe-edit-category" value={form.category} onChange={set("category")} options={RECIPE_CATEGORIES} />
          <Area label="Introduction" testid="recipe-edit-intro" value={form.intro} onChange={set("intro")} rows={3} />

          <div className="grid grid-cols-2 gap-2">
            <Field label="Prep time" testid="recipe-edit-prep" value={form.prep_time} onChange={set("prep_time")} />
            <Field label="Cook time" testid="recipe-edit-cook" value={form.cook_time} onChange={set("cook_time")} />
            <Field label="Total time" testid="recipe-edit-total" value={form.total_time} onChange={set("total_time")} />
            <Field label="Serving size" testid="recipe-edit-serves" value={form.serves} onChange={set("serves")} />
          </div>

          <Area label="Ingredients (one per line)" testid="recipe-edit-ingredients" value={form.ingredients} onChange={set("ingredients")} rows={6} />
          <Area label="Instructions (one step per line)" testid="recipe-edit-steps" value={form.steps} onChange={set("steps")} rows={6} />

          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Nutrition per serving</p>
            <div className="grid grid-cols-2 gap-2">
              {NUTRITION_KEYS.map((k) => (
                <Field key={k} label={k} testid={`recipe-edit-nutrition-${k.toLowerCase()}`} value={form.nutrition[k] || ""} onChange={setNut(k)} />
              ))}
            </div>
          </div>

          <Area label="Healthy tips (one per line)" testid="recipe-edit-tips" value={form.tips} onChange={set("tips")} rows={3} />
          <Area label="Portion guidance" testid="recipe-edit-portion" value={form.portion} onChange={set("portion")} rows={2} />
          <Area label="Suitable for / dietary notes (one per line)" testid="recipe-edit-suitable" value={form.suitable_for} onChange={set("suitable_for")} rows={2} />

          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Tags</p>
            <div className="flex flex-wrap gap-1.5">
              {RECIPE_TAGS.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`recipe-edit-tag-${t.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                  onClick={() => toggleTag(t)}
                  className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                    form.tags.includes(t)
                      ? "bg-emerald-700 text-white border-emerald-700"
                      : "bg-white dark:bg-emerald-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-emerald-800"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between py-1">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Visible to users</span>
            <Switch data-testid="recipe-edit-active" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
          </div>

          <button
            data-testid="recipe-edit-save"
            onClick={save}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-3 rounded-2xl transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const inputCls = "w-full px-3.5 py-2.5 rounded-xl bg-white dark:bg-emerald-900/60 border border-slate-200 dark:border-emerald-800 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40";

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
    <input data-testid={testid} {...props} className={`${inputCls} mt-1`} />
  </label>
);

const Area = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
    <textarea data-testid={testid} {...props} className={`${inputCls} mt-1 resize-none`} />
  </label>
);

const SelectField = ({ label, testid, options, ...props }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
    <select data-testid={testid} {...props} className={`${inputCls} mt-1`}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);
